import { type ReactNode, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

const ANIMATION_DURATION = 280

interface ViewTransitionProps {
	children: ReactNode
	stateKey: string
	className?: string
}

type Snapshot = {
	key: string
	node: ReactNode
}

function usePrefersReducedMotion() {
	const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)

	useEffect(() => {
		if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
			return undefined
		}

		const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
		const handleChange = (event: MediaQueryListEvent) => {
			setPrefersReducedMotion(event.matches)
		}

		setPrefersReducedMotion(mediaQuery.matches)

		if (typeof mediaQuery.addEventListener === 'function') {
			mediaQuery.addEventListener('change', handleChange)
			return () => mediaQuery.removeEventListener('change', handleChange)
		}

		mediaQuery.addListener(handleChange)
		return () => mediaQuery.removeListener(handleChange)
	}, [])

	return prefersReducedMotion
}

export function ViewTransition({ children, stateKey, className }: ViewTransitionProps) {
	const prefersReducedMotion = usePrefersReducedMotion()
	const [active, setActive] = useState<Snapshot>(() => ({ key: stateKey, node: children }))
	const [previous, setPrevious] = useState<Snapshot | null>(null)
	const [animating, setAnimating] = useState(false)

	const activeRef = useRef(active)
	const timeoutRef = useRef<number | null>(null)

	useEffect(() => {
		activeRef.current = active
	}, [active])

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				window.clearTimeout(timeoutRef.current)
			}
		}
	}, [])

	useEffect(() => {
		if (stateKey === activeRef.current.key) {
			setActive({ key: stateKey, node: children })
			return
		}

		if (prefersReducedMotion) {
			setPrevious(null)
			setAnimating(false)
			setActive({ key: stateKey, node: children })
			return
		}

		setPrevious(activeRef.current)
		setActive({ key: stateKey, node: children })
		setAnimating(true)

		if (timeoutRef.current) {
			window.clearTimeout(timeoutRef.current)
		}

		timeoutRef.current = window.setTimeout(() => {
			setPrevious(null)
			setAnimating(false)
			timeoutRef.current = null
		}, ANIMATION_DURATION)
	}, [children, stateKey, prefersReducedMotion])

	return (
		<div className={cn('view-transition-stack', className)}>
			{previous && !prefersReducedMotion && (
				<div className="view-transition-layer view-transition-layer--exit" aria-hidden>
					{previous.node}
				</div>
			)}
			<div
				className={cn(
					'view-transition-layer',
					previous && animating && !prefersReducedMotion ? 'view-transition-layer--enter' : undefined
				)}
			>
				{active.node}
			</div>
		</div>
	)
}
